import React from 'react';
import { Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../theme/tokens';

/**
 * BrandSwitch - the one toggle this app renders, in both scripts.
 *
 * WHY THIS FILE EXISTS: MUI'S OFFSETS ARE PHYSICAL
 * `@mui/material/Switch` positions its knob with PHYSICAL CSS: the switch base
 * carries `left: 0` and its checked state carries `transform: translateX(20px)`
 * (see node_modules/@mui/material/Switch/Switch.js). Neither is a logical
 * property, and this project mounts NO RTL MUI theme and no
 * `stylis-plugin-rtl`, so nothing flips them. Under `dir="rtl"` every stock MUI
 * switch therefore rests on the physical LEFT and slides further RIGHT when
 * turned on - the mirror image of how a Hebrew reader expects a switch to
 * behave (rest at the inline START, travel toward the inline END).
 *
 * Two hand-rolled fixes already existed for this - one in
 * `SimpleAccessibilitySettings.js`, one in `CookieConsent/CookieConsentBanner.js`
 * - and both hardcode RTL: they pin the knob to `right` and negate the
 * transform unconditionally, which is correct only because those two panels are
 * always Hebrew. The settings folder is not (it renders `dir="ltr"` for an
 * English user), so the fix has to READ the direction rather than assume it.
 *
 * HOW THE DIRECTION IS READ - AND WHY NOT `theme.direction`
 * `theme.direction` is `'ltr'` everywhere in this app: no RTL theme is ever
 * created, so it would answer LTR inside a `dir="rtl"` panel and the bug would
 * survive. The direction that is actually true is the `dir` ATTRIBUTE on the
 * nearest ancestor that declares one, so the RTL rules hang off
 * `[dir="rtl"] &` (plus `&[dir="rtl"]`, for a switch that carries its own).
 * That selector also outranks both MUI's rules and this file's own LTR rules on
 * specificity, so the flip does not depend on stylesheet insertion order.
 *
 * `:dir(rtl)` would be the tidier selector and is deliberately NOT used: it is
 * unsupported in older Safari and in jsdom, and an unsupported selector inside
 * a comma-separated list invalidates the WHOLE list - so adding it would silently
 * drop the `[dir="rtl"]` rule beside it rather than degrade.
 *
 * SIZES
 *   medium - 46x26 track, 20px thumb. The accessibility dialog's switch,
 *            reproduced exactly (this file replaced its local copy).
 *   small  - 34x20 track, 16px thumb. The settings rows.
 * Both are drawn from `SIZES` below; the checked travel is DERIVED
 * (`width - thumb - 2 * inset`), never typed, so the two directions can never
 * disagree about how far the knob moves.
 *
 * TAP TARGET: the visible track is shorter than 24px at `small`, so the root
 * box is 24px tall with 2px of vertical padding (the track fills the content
 * box and still renders at 20px), and the hidden `<input>` is stretched to the
 * full root height in both sizes. The pointer target is 24px tall at `small`
 * and 26px at `medium` while nothing visible moves.
 *
 * COLOUR: checked track is `colors.primary.accessible` (#005A52) under a white
 * thumb. That pair is 8.1:1, and the track is the same 8.1:1 against a white
 * surface - well past the 3:1 WCAG 1.4.11 asks of a control. It is the token
 * the accessibility dialog already used; the settings folder's `brandAccent`
 * (#0D9488) was the other candidate and is only 3.7:1, so the deeper teal wins
 * and both surfaces are now signed the same. The OFF track is a mid grey
 * (#949494, 3:1 against the white thumb and against a white surface); the two
 * original copies used `colors.neutral[300]`, which measured 1.3:1 and read as
 * no switch at all once the small size landed on the settings rows.
 *
 * MOTION: the knob's transition is dropped under `html.lf-motion-off` (the
 * accessibility widget's "stop animations" and `prefers-reduced-motion`, via
 * `motion/motionSignal.js`) and under the media query directly, so a page that
 * never mounts the motion signal still honours the OS setting.
 */

/** width/height/thumb/inset per size. Everything else is derived from these. */
const SIZES = {
  medium: { width: 46, trackHeight: 26, thumb: 20, inset: 3, padY: 0 },
  small: { width: 34, trackHeight: 20, thumb: 16, inset: 2, padY: 2 },
};

const TRACK_ON = colors.primary.accessible;
// OFF track: a mid grey rather than the tokens' neutral[300] (#E0E0E0), which
// measures 1.3:1 against the white thumb and reads as "no switch" - #949494 is
// 3:1 against the thumb and against a white surface (reviewer finding, 2026-09-17).
const TRACK_OFF = '#949494';

const StyledSwitch = styled(Switch, {
  // Consumed for styling only. `size` itself is never forwarded to MUI (see
  // the wrapper below), so MUI's own `size="small"` variant - which hardcodes
  // its own physical `translateX(16px)` - never enters the cascade.
  shouldForwardProp: (prop) => prop !== 'ownerSize',
})(({ ownerSize }) => {
  const { width, trackHeight, thumb, inset, padY } = SIZES[ownerSize];
  const travel = width - thumb - inset * 2;
  const rootHeight = trackHeight + padY * 2;
  // Absolutely positioned children resolve against the PADDING box, so the
  // knob's own offset has to carry the root's vertical padding as well.
  const thumbTop = padY + inset;

  return {
    width,
    height: rootHeight,
    padding: padY ? `${padY}px 0` : 0,

    '& .MuiSwitch-switchBase': {
      padding: 0,
      top: thumbTop,
      // LTR resting anchor. `right: auto` is not decoration: MUI's own
      // `left: 0` and this rule must never both be live, or the base is
      // stretched between the two edges.
      left: inset,
      right: 'auto',
      transitionDuration: '200ms',

      '&.Mui-checked': {
        transform: `translateX(${travel}px)`,
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: TRACK_ON,
          opacity: 1,
        },
      },

      // MUI fades a disabled switch's track to `opacity: 0.12`, which against
      // a white surface is barely a switch at all. The dimming of a disabled
      // control belongs to the CALLER, which knows how much of the row is
      // inactive (`AnonymousAuthorPrefRow` dims the control to 0.55 and
      // deliberately leaves the prose beside it at full strength), so the
      // track keeps its own colour and this rule out-specifies MUI's rather
      // than tying with it.
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 1,
      },

      // MUI's SwitchBase toggles this CLASS through its own focus-visible
      // detection rather than the native pseudo, so it never collides with
      // `styles/simple-accessibility.css`'s global `*:focus-visible` rule.
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        outline: `2px solid ${TRACK_ON}`,
        outlineOffset: 2,
      },
    },

    // The pointer target, stretched to the full root height. Purely
    // hit-testing: the input is transparent and MUI already spans it to 300%
    // of the base's width.
    '& .MuiSwitch-input': {
      top: -thumbTop,
      height: rootHeight,
    },

    '& .MuiSwitch-thumb': {
      width: thumb,
      height: thumb,
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
    },

    '& .MuiSwitch-track': {
      borderRadius: trackHeight / 2,
      backgroundColor: TRACK_OFF,
      opacity: 1,
      transition: 'background-color 200ms',
    },

    // ── The whole point of this component ────────────────────────────────
    // Same knob, mirrored: rest at the inline start, travel toward the inline
    // end. Higher specificity than every rule above and than MUI's own, so
    // this wins wherever a `dir="rtl"` ancestor exists and is inert elsewhere.
    '[dir="rtl"] &, &[dir="rtl"]': {
      '& .MuiSwitch-switchBase': {
        left: 'auto',
        right: inset,
        '&.Mui-checked': {
          transform: `translateX(-${travel}px)`,
        },
      },
    },

    'html.lf-motion-off &': {
      '& .MuiSwitch-switchBase': { transition: 'none' },
      '& .MuiSwitch-track': { transition: 'none' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& .MuiSwitch-switchBase': { transition: 'none' },
      '& .MuiSwitch-track': { transition: 'none' },
    },
  };
});

/**
 * @param {'small'|'medium'} [size='medium']  Which row this switch sits in.
 * Every other prop - `checked`, `onChange`, `disabled`, `name`, `inputProps`,
 * `sx`, `aria-*` - is forwarded to MUI's `<Switch>` untouched, so this stays a
 * drop-in replacement for one.
 */
const BrandSwitch = React.forwardRef(function BrandSwitch({ size, ...props }, ref) {
  // A total mapping, deliberately not `SIZES[size] ? size : 'medium'` - that
  // form answers truthy for an inherited key like `constructor` and hands the
  // styled function something it cannot destructure.
  const ownerSize = size === 'small' ? 'small' : 'medium';

  return <StyledSwitch ref={ref} ownerSize={ownerSize} {...props} />;
});

export default BrandSwitch;
